<template>
  <div>
    <div
      class="card-item hover-container"
      :style="{ 'border-color': cardTypeColor }"
      :class="{ 'height-transition-hover-container': shouldExpandOnHover }"
    >
      <select-read-model-combobox
        v-if="showReadModelCombobox"
        :events="[event]"
        :cardIds="[item.id]"
        :style="{ '--borderColor': cardTypeColor, '--borderTopWidth': '1px' }"
        :class="{ 'size-small': miniMode }"
      />
      <select-entity-combobox
        v-if="showCombobox"
        :events="[event]"
        :cardIds="[item.id]"
        :schemaType="comboboxSchemaType"
        :style="{ '--borderColor': cardTypeColor, '--borderTopWidth': '1px' }"
        :class="{ 'size-small': miniMode }"
      />
      <tag-input
        v-if="item.cardType?.domainModelRole === DomainModelRole.TAG"
        class="pa-1"
        :cardId="item.id"
      />
      <slot v-if="!showCombobox && !showReadModelCombobox && item.cardType?.domainModelRole !== DomainModelRole.TAG">
        <text-editor
          class="pa-1"
          :id="item.id"
          :text="item.description"
          :disabled="$store.state.isWorkflowDisabled"
          @text-update="updateDescription($event, item.id)"
          @text-delta="$emit('text-delta')"
          @move-focus="moveFocus"
          @keydown.native.stop=""
          :borderColor="cardTypeColor"
        />
      </slot>
      <slot name="fields" />
      <v-container fluid :class="{ 'pa-0': true, 'mt-0': showAdd, 'mt-3': !showAdd }">
        <v-row class="ma-0 align-center">
          <v-col class="pa-0">
            <Suspense>
              <template #default>
                <card-actions
                  :card="item"
                  :show-as-invisible="showAsInvisible"
                  :jiraData2="jiraData2"
                  :loadingJiraInfo="loadingJiraInfo"
                  :with-dates="withDates"
                  :date-created="item.created"
                  :date-updated="item.updated"
                  :creator="item.creator"
                  @open-replies="openReplies"
                  :hideTypeSelector="hideTypeSelector"
                  :hideOpenInSidebar="hideOpenInSidebar"
                  :showAdd="showAdd"
                  :hideCreator="hideCreator"
                  :hideReleaseSelector="hideReleaseSelector"
                  :hideActions="hideActions"
                  :showCommentsCount="showCommentsCount"
                ></card-actions>
              </template>
              <template #fallback>
                <div class="loading-text">Loading...</div>
              </template>
            </Suspense>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <card-replies
      v-if="!hideReplies"
      class="my-2"
      :showAll="showAll"
      :cardId="item.id"
      :replies="item.comments"
      :expand="repliesOpened || showAll"
      :reply-form-visible="replyFormVisible"
      :with-dates="replyWithDates"
      :hide-avatar="hideAvatar"
      :showAi="showAi"
      :mini-mode="miniMode"
      @expand-change="handleExpandChange"
      @expand-transition-before="$emit('expand-transition-before')"
      @expand-transition-after="$emit('expand-transition-after')"
      @hide-reply-form="replyFormVisible = false"
      @ask-for-reply="$emit('ask-for-reply', $event)"
      @open-replies="openReplies"
    ></card-replies>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { mapState } from 'vuex'
import TagInput from '@/components/TagInput.vue'
import CardReplies from '@/components/CardReplies'
import TextEditor from '@/components/TextEditor.vue'
import SelectEntityCombobox from '@/components/SelectEntityCombobox.vue'
import SelectReadModelCombobox from '@/components/SelectReadModelCombobox.vue'
import { SchemaType } from '@/constants/schema'
import { DomainModelRole } from '@/constants/cardTypes'
import workflowApi from '@/api/workflowApi'
import cardTypesApi from '@/api/cardTypesApi'

export default {
  components: {
    SelectReadModelCombobox,
    SelectEntityCombobox,
    CardReplies,
    TextEditor,
    TagInput,
    CardActions: defineAsyncComponent(() => import('@/components/CardActions'))
  },

  props: {
    item: Object,
    event: Object,
    withDates: Boolean,
    replyWithDates: Boolean,
    jiraData2: Object,
    loadingJiraInfo: Number,
    hideAvatar: Boolean,
    hideTypeSelector: Boolean,
    hideReleaseSelector: Boolean,
    hideOpenInSidebar: Boolean,
    hideActions: Boolean,
    hideReplies: Boolean,
    hideThumbs: Boolean,
    hideComments: Boolean,
    showAi: Boolean,
    showEntitySelector: Boolean,
    showAll: Boolean,
    showAsInvisible: Boolean,
    boundedContexts: Array,
    showCommentsCount: Boolean,
    defaultReplyFormVisible: Boolean,
    miniMode: Boolean,
    showAdd: Boolean,
    hideCreator: Boolean,
    expandOnHover: Boolean
  },

  data () {
    return {
      repliesOpened: false,
      replyFormVisible: false,
      keyDownPressed: false,
      DomainModelRole
    }
  },

  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow
    }),
    cardTypeColor () {
      if (this.showAsInvisible) {
        return '#868686'
      }

      const { cardType } = this.item
      return cardTypesApi.getBackgroundColour(cardType)
    },
    shouldExpandOnHover () {
      return !!this.expandOnHover && [
        DomainModelRole.COMMAND,
        DomainModelRole.READ_MODEL,
        DomainModelRole.AGGREGATE_ROOT,
        DomainModelRole.DOMAIN_EVENT
      ].includes(this.item.cardType?.domainModelRole)
    },
    showReadModelCombobox () {
      return this.showEntitySelector &&
        this.item.cardType?.domainModelRole === DomainModelRole.READ_MODEL &&
        this.event
    },
    showCombobox () {
      return this.showEntitySelector &&
        [DomainModelRole.AGGREGATE_ROOT, DomainModelRole.DOMAIN_EVENT].includes(this.item.cardType?.domainModelRole) &&
        this.event
    },
    comboboxSchemaType () {
      if (this.item.cardType?.domainModelRole === DomainModelRole.DOMAIN_EVENT) {
        return SchemaType.DOMAIN_EVENT
      }
      return SchemaType.ENTITY
    }
  },

  methods: {
    moveFocus (direction, target) {
      target.firstChild.blur()
    },
    updateDescription (html, itemId) {
      workflowApi.updateCardsMutation(this.$route.params.id, [{ cardId: itemId, description: html }])
    },
    openReplies () {
      this.$emit('open-replies', this.item.id)
      this.repliesOpened = true
      this.replyFormVisible = true
    },

    handleExpandChange (expand) {
      if (expand) {
        this.repliesOpened = true
      } else {
        this.repliesOpened = false
        this.replyFormVisible = false
      }
    }
  },
  mounted () {
    this.replyFormVisible = this.defaultReplyFormVisible || false
  }
}
</script>

<style scoped>
  .card-item {
    overflow: hidden;
    padding: 12px;
    border-radius: 8px;
    background: var(--light-1);
    border-width: 2px 1px 1px 1px;
    border-style: solid;
    border-color: var(--text-dark);
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.15);
  }

  .card-item-handle {
    width: 24px;
    height: 4px;
    margin-bottom: 9px;
    border-radius: 10px;
    background: black;
  }

  .text-nowrap {
    white-space: nowrap;
  }

  .v-tooltip__content {
    background: var(--light-1);
    color: var(--color-primary);
    border-radius: 10px;
  }

  .loading-text {
    font-size: 11px;
  }
</style>
